import api from '../config/api';

const API_BASE = process.env.REACT_APP_API_URL || 'https://api.superchargeme.com';

/**
 * Inventory Service - API client for inventory management
 * Handles warehouses, batches, COGS, transfers, returns, fulfillment config, physical counts
 */

// ============================================================================
// WAREHOUSES
// ============================================================================

export const getWarehouses = async (params = {}) => {
  const response = await api.get(`${API_BASE}/api/warehouses`, { params });
  return response.data;
};

export const getWarehouse = async (id) => {
  const response = await api.get(`${API_BASE}/api/warehouses/${id}`);
  return response.data;
};

export const getWarehouseByCode = async (code) => {
  const response = await api.get(`${API_BASE}/api/warehouses/code/${code}`);
  return response.data;
};

export const createWarehouse = async (data) => {
  const response = await api.post(`${API_BASE}/api/warehouses`, data);
  return response.data;
};

export const updateWarehouse = async (id, data) => {
  const response = await api.put(`${API_BASE}/api/warehouses/${id}`, data);
  return response.data;
};

export const deactivateWarehouse = async (id) => {
  const response = await api.post(`${API_BASE}/api/warehouses/${id}/deactivate`);
  return response.data;
};

export const reactivateWarehouse = async (id) => {
  const response = await api.post(`${API_BASE}/api/warehouses/${id}/reactivate`);
  return response.data;
};

export const getWarehouseInventory = async (id, params = {}) => {
  const response = await api.get(`${API_BASE}/api/warehouses/${id}/inventory`, { params });
  return response.data;
};

// ============================================================================
// WAREHOUSE × BRAND → SUBSIDIARY MAPPINGS
// ============================================================================

export const getBrandSubsidiaryMappings = async () => {
  const response = await api.get(`${API_BASE}/api/warehouses/brand-subsidiary-mappings`);
  return response.data;
};

export const upsertBrandSubsidiaryMapping = async (data) => {
  const response = await api.post(`${API_BASE}/api/warehouses/brand-subsidiary-mappings`, data);
  return response.data;
};

export const deleteBrandSubsidiaryMapping = async (id) => {
  const response = await api.delete(`${API_BASE}/api/warehouses/brand-subsidiary-mappings/${id}`);
  return response.data;
};

// ============================================================================
// PRICE TIERS
// ============================================================================

export const listPriceTiers = async () => {
  const response = await api.get(`${API_BASE}/api/price-tiers`);
  return response.data;
};
// By-SKU view (price-tiers redesign, Rishabh 2026-07-03)
export const listPriceTiersBySku = async (params) => {
  const response = await api.get(`${API_BASE}/api/price-tiers/by-sku`, { params });
  return response.data;
};
export const getPriceTierSkuTiers = async (sku) => {
  const response = await api.get(`${API_BASE}/api/price-tiers/by-sku/${encodeURIComponent(sku)}/tiers`);
  return response.data;
};
export const getPriceTier = async (id) => {
  const response = await api.get(`${API_BASE}/api/price-tiers/${id}`);
  return response.data;
};
export const createPriceTier = async (data) => {
  const response = await api.post(`${API_BASE}/api/price-tiers`, data);
  return response.data;
};
export const updatePriceTier = async (id, data) => {
  const response = await api.put(`${API_BASE}/api/price-tiers/${id}`, data);
  return response.data;
};
export const upsertPriceTierItems = async (id, items) => {
  const response = await api.post(`${API_BASE}/api/price-tiers/${id}/items`, items);
  return response.data;
};
export const deletePriceTierItem = async (id, itemId) => {
  const response = await api.delete(`${API_BASE}/api/price-tiers/${id}/items/${itemId}`);
  return response.data;
};
// Product-master typeahead for the tier item picker (SKU or name → real product).
export const searchTierProducts = async (q) => {
  const response = await api.get(`${API_BASE}/api/price-tiers/products/search`, { params: { q } });
  return response.data;
};

// ============================================================================
// CIN7 PORT CONFIG (multi-tenant template editor)
// ============================================================================
export const listCin7PortConfigs = async (params = {}) => {
  const r = await api.get(`${API_BASE}/api/cin7-port-config`, { params });
  return r.data;
};
export const listCin7PortConfigScopes = async () => {
  const r = await api.get(`${API_BASE}/api/cin7-port-config/scopes`);
  return r.data;
};
export const upsertCin7PortConfig = async (payload) => {
  const r = await api.post(`${API_BASE}/api/cin7-port-config`, payload);
  return r.data;
};
export const deactivateCin7PortConfig = async (id) => {
  const r = await api.delete(`${API_BASE}/api/cin7-port-config/${id}`);
  return r.data;
};

// ============================================================================
// COMMERCIAL INVOICES
// ============================================================================

export const listCommercialInvoices = async (params = {}) => {
  const response = await api.get(`${API_BASE}/api/commercial-invoices`, { params });
  return response.data;
};
export const getCommercialInvoice = async (id) => {
  const response = await api.get(`${API_BASE}/api/commercial-invoices/${id}`);
  return response.data;
};
export const generateCommercialInvoiceFromTransfer = async (transferId) => {
  const response = await api.post(`${API_BASE}/api/commercial-invoices/from-transfer/${transferId}`);
  return response.data;
};
export const updateCommercialInvoiceItem = async (id, itemId, data) => {
  const response = await api.put(`${API_BASE}/api/commercial-invoices/${id}/items/${itemId}`, data);
  return response.data;
};
export const commercialInvoicePreviewUrl = (id, kind = 'invoice') => {
  return `${API_BASE}/api/commercial-invoices/${id}/preview?kind=${kind}`;
};
// PDF endpoint URL — used by the Download PDF action.
export const commercialInvoicePdfUrl = (id, kind = 'invoice') => {
  return `${API_BASE}/api/commercial-invoices/${id}/pdf?kind=${kind}`;
};

// ============================================================================
// INVENTORY BATCHES
// ============================================================================

export const getInventoryBatches = async (params = {}) => {
  const response = await api.get(`${API_BASE}/api/procurement/inventory-batches`, { params });
  return response.data;
};

export const getExpiringBatches = async (params = {}) => {
  const response = await api.get(`${API_BASE}/api/procurement/inventory-batches/expiring`, { params });
  return response.data;
};

// ============================================================================
// EXTERNAL STOCK (Amazon FBA, etc.)
// ============================================================================

export const getExternalStockLevels = async (brandId, params = {}) => {
  const response = await api.get(`${API_BASE}/api/brands/${brandId}/external-stock`, { params });
  return response.data;
};

export const getExternalWarehouses = async (brandId) => {
  const response = await api.get(`${API_BASE}/api/brands/${brandId}/external-warehouses`);
  return response.data;
};

// ============================================================================
// CALCULATED STOCK LEVELS
// ============================================================================

/**
 * Get calculated stock levels aggregated by parent SKU
 * Combines batches + FBA - disposals with warehouse breakdown
 */
export const getCalculatedStockLevels = async (params = {}) => {
  const response = await api.get(`${API_BASE}/api/stock-levels`, { params });
  return response.data;
};

/**
 * Get stock summary by warehouse
 */
export const getStockSummary = async (params = {}) => {
  const response = await api.get(`${API_BASE}/api/stock-levels/summary`, { params });
  return response.data;
};

/**
 * Get detailed batches for a specific SKU
 */
export const getSkuBatches = async (sku, params = {}) => {
  const response = await api.get(`${API_BASE}/api/stock-levels/sku/${encodeURIComponent(sku)}/batches`, { params });
  return response.data;
};

/**
 * Get batch data for multiple SKUs in one request
 */
export const getBulkSkuBatches = async (skus, params = {}) => {
  const response = await api.post(`${API_BASE}/api/stock-levels/bulk-batches`, { skus, ...params });
  return response.data;
};

/**
 * Get shipped orders by SKU (for analysis)
 */
export const getShippedOrdersBySku = async (params = {}) => {
  const response = await api.get(`${API_BASE}/api/stock-levels/shipped-orders`, { params });
  return response.data;
};

/**
 * Get inventory SKUs for mapping dropdowns
 */
export const getInventorySkus = async (params = {}) => {
  const response = await api.get(`${API_BASE}/api/stock-levels/inventory-skus`, { params });
  return response.data;
};

/**
 * Get unmapped SKUs (SKUs from orders/transfers not mapped to inventory)
 */
export const getUnmappedSkus = async (params = {}) => {
  const response = await api.get(`${API_BASE}/api/stock-levels/unmapped-skus`, { params });
  return response.data;
};

/**
 * Get inventory dashboard summary (total SKUs, units, value, low stock, expiring)
 * Uses movement-based calculations for accurate totals
 */
export const getDashboardSummary = async (brandId) => {
  const response = await api.get(`${API_BASE}/api/stock-levels/dashboard-summary`, {
    params: { brand_id: brandId }
  });
  return response.data;
};

/**
 * Get stock count dates for a specific SKU and warehouse
 * Returns array of { count_id, count_date, counted_quantity }
 */
export const getStockCountDates = async (sku, warehouseCode, params = {}) => {
  const response = await api.get(
    `${API_BASE}/api/stock-levels/stock-count-dates/${encodeURIComponent(sku)}/${encodeURIComponent(warehouseCode)}`,
    { params }
  );
  return response.data;
};

/**
 * Get all trust points for a specific SKU and warehouse
 * Trust points are reliable starting points for stock calculation:
 * - Physical counts
 * - Goods receipts (factory/supplier shipments)
 * - Transfers in (from other warehouses)
 *
 * @param {string} sku - The SKU
 * @param {string} warehouseCode - The warehouse code
 * @param {Object} params - Additional params: brand_id, as_of_date
 * @returns {Object} { physical_counts: [], goods_receipts: [], transfers_in: [] }
 */
export const getTrustPoints = async (sku, warehouseCode, params = {}) => {
  const response = await api.get(
    `${API_BASE}/api/stock-levels/trust-points/${encodeURIComponent(sku)}/${encodeURIComponent(warehouseCode)}`,
    { params }
  );
  return response.data;
};

/**
 * Export stock levels to CSV with batch details (batch_number, expiry_date)
 * Downloads file with proper authentication
 * @param {number} brandId - Required brand ID
 * @param {string} asOfDate - Optional date in YYYY-MM-DD format
 */
export const exportStockLevels = async (brandId, asOfDate = null, opts = {}) => {
  if (!brandId) {
    throw new Error('Brand ID is required for export');
  }

  let url = `${API_BASE}/api/stock-levels/export?brand_id=${brandId}`;
  if (asOfDate) {
    url += `&as_of_date=${asOfDate}`;
  }
  // Optional export filters (level / status / in-stock / warehouse). Arrays are sent
  // as comma lists (multi-select); a single string still works.
  const joinFilter = (v) => Array.isArray(v) ? v.join(',') : (v && v !== 'all' ? v : '');
  const lvl = joinFilter(opts.level); if (lvl) url += `&level=${encodeURIComponent(lvl)}`;
  const sts = joinFilter(opts.status); if (sts) url += `&status=${encodeURIComponent(sts)}`;
  if (opts.inStock) url += `&in_stock=1`;
  if (opts.includeVariance) url += `&include_variance=1`;
  const whs = joinFilter(opts.warehouse); if (whs) url += `&warehouse=${encodeURIComponent(whs)}`;

  try {
    const response = await api.get(url, { responseType: 'blob' });

    // Create download link
    const blob = new Blob([response.data], { type: 'text/csv' });
    const downloadUrl = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = downloadUrl;

    // Get filename from content-disposition header or use default
    const contentDisposition = response.headers['content-disposition'];
    let filename = `stock_levels_${asOfDate || 'export'}.csv`;
    if (contentDisposition) {
      const match = contentDisposition.match(/filename="?(.+)"?/);
      if (match && match[1]) {
        filename = match[1].replace(/"/g, '');
      }
    }

    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(downloadUrl);

    return { success: true };
  } catch (error) {
    console.error('Error exporting stock levels:', error);
    throw error;
  }
};

/**
 * Export detailed stock levels to CSV WITH movement breakdown columns
 * Includes opening balance, receipts, transfers, shipments, returns, disposals
 * @param {number} brandId - Required brand ID
 * @param {string} asOfDate - Optional date in YYYY-MM-DD format
 */
export const exportStockLevelsDetailed = async (brandId, asOfDate = null, opts = {}) => {
  if (!brandId) {
    throw new Error('Brand ID is required for detailed export');
  }

  let url = `${API_BASE}/api/stock-levels/export-detailed?brand_id=${brandId}`;
  if (asOfDate) {
    url += `&as_of_date=${asOfDate}`;
  }
  // Optional export filters (level / status / in-stock / warehouse). Arrays are sent
  // as comma lists (multi-select); a single string still works.
  const joinFilter = (v) => Array.isArray(v) ? v.join(',') : (v && v !== 'all' ? v : '');
  const lvl = joinFilter(opts.level); if (lvl) url += `&level=${encodeURIComponent(lvl)}`;
  const sts = joinFilter(opts.status); if (sts) url += `&status=${encodeURIComponent(sts)}`;
  if (opts.inStock) url += `&in_stock=1`;
  if (opts.includeVariance) url += `&include_variance=1`;
  const whs = joinFilter(opts.warehouse); if (whs) url += `&warehouse=${encodeURIComponent(whs)}`;

  try {
    const response = await api.get(url, { responseType: 'blob' });

    // Create download link
    const blob = new Blob([response.data], { type: 'text/csv' });
    const downloadUrl = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = downloadUrl;

    // Get filename from content-disposition header or use default
    const contentDisposition = response.headers['content-disposition'];
    let filename = `stock_levels_detailed_${asOfDate || 'export'}.csv`;
    if (contentDisposition) {
      const match = contentDisposition.match(/filename="?(.+)"?/);
      if (match && match[1]) {
        filename = match[1].replace(/"/g, '');
      }
    }

    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(downloadUrl);

    return { success: true };
  } catch (error) {
    console.error('Error exporting detailed stock levels:', error);
    throw error;
  }
};

/**
 * Get stock breakdown from a specific trust point
 * Returns opening balance from selected trust point and all movements after that date
 * Supports: physical_count (default), goods_receipt, transfer_in
 *
 * @param {string} sku - The SKU
 * @param {string} warehouseCode - The warehouse code
 * @param {string} fromCountDate - The date to use as opening balance (YYYY-MM-DD)
 * @param {Object} params - Additional params: brand_id, as_of_date, trust_point_type, trust_point_id
 */
export const getStockBreakdownFromCount = async (sku, warehouseCode, fromCountDate, params = {}) => {
  const response = await api.get(
    `${API_BASE}/api/stock-levels/breakdown/${encodeURIComponent(sku)}/${encodeURIComponent(warehouseCode)}`,
    { params: { ...params, from_count_date: fromCountDate } }
  );
  return response.data;
};

/**
 * Get stock breakdown from a specific trust point (wrapper with clearer naming)
 * @param {string} sku - The SKU
 * @param {string} warehouseCode - The warehouse code
 * @param {Object} trustPoint - The trust point { type, id, date }
 * @param {Object} params - Additional params: brand_id, as_of_date
 */
export const getStockBreakdownFromTrustPoint = async (sku, warehouseCode, trustPoint, params = {}, currentStock = null) => {
  const queryParams = {
    ...params,
    from_count_date: trustPoint.date,
    trust_point_type: trustPoint.type,
    trust_point_id: trustPoint.id
  };
  // When the frontend knows the current stock (from the main stock levels page),
  // pass it to skip a second heavy query in the backend
  if (currentStock !== null && currentStock !== undefined) {
    queryParams.current_stock = currentStock;
  }
  const response = await api.get(
    `${API_BASE}/api/stock-levels/breakdown/${encodeURIComponent(sku)}/${encodeURIComponent(warehouseCode)}`,
    { params: queryParams }
  );
  return response.data;
};

/**
 * Create a SKU alias mapping (link order SKU to inventory SKU)
 */
export const createSkuAliasMapping = async (aliasSku, primarySku, notes = '') => {
  const response = await api.post(`${API_BASE}/api/stock-levels/sku-alias`, {
    alias_sku: aliasSku,
    primary_sku: primarySku,
    notes
  });
  return response.data;
};

/**
 * Create a new inventory SKU in product_master
 */
export const createNewInventorySku = async (sku, productName, brandId) => {
  const response = await api.post(`${API_BASE}/api/stock-levels/create-inventory-sku`, {
    sku,
    product_name: productName,
    brand_id: brandId
  });
  return response.data;
};

// ============================================================================
// COGS MANAGEMENT
// ============================================================================

export const getCogs = async (brandId, params = {}) => {
  const response = await api.get(`${API_BASE}/api/cogs/brands/${brandId}/cogs`, { params });
  return response.data;
};

export const createOrUpdateCogs = async (brandId, cogsArray, extra = {}) => {
  // `extra` carries TOP-LEVEL fields alongside the cogs[] array (e.g. category,
  // sub_category applied to product_master for every SKU in the batch).
  const response = await api.post(`${API_BASE}/api/cogs/brands/${brandId}/cogs`, { cogs: cogsArray, ...extra });
  return response.data;
};

export const updateCogs = async (brandId, sku, data) => {
  const response = await api.put(`${API_BASE}/api/cogs/brands/${brandId}/cogs/${encodeURIComponent(sku)}`, data);
  return response.data;
};

export const deleteCogs = async (brandId, sku) => {
  const response = await api.delete(`${API_BASE}/api/cogs/brands/${brandId}/cogs/${encodeURIComponent(sku)}`);
  return response.data;
};

export const getCogsHistory = async (brandId, sku) => {
  const response = await api.get(`${API_BASE}/api/cogs/brands/${brandId}/cogs/${encodeURIComponent(sku)}/history`);
  return response.data;
};

export const createBundle = async (brandId, bundleData) => {
  const response = await api.post(`${API_BASE}/api/cogs/brands/${brandId}/bundles`, bundleData);
  return response.data;
};

export const bulkImportCogs = async (brandId, csvData) => {
  const response = await api.post(`${API_BASE}/api/cogs/brands/${brandId}/cogs/bulk-import`, csvData);
  return response.data;
};

export const getAliases = async (brandId) => {
  const params = brandId ? { brand_id: brandId } : {};
  const response = await api.get(`${API_BASE}/api/cogs/aliases`, { params });
  return response.data;
};

export const createAlias = async (data) => {
  const response = await api.post(`${API_BASE}/api/cogs/aliases`, data);
  return response.data;
};

export const deleteAlias = async (sku) => {
  const response = await api.delete(`${API_BASE}/api/cogs/aliases/${encodeURIComponent(sku)}`);
  return response.data;
};

// Aliases for ProductCOGS component compatibility
export const getProductCosts = getCogs;
export const createProductCost = async (brandId, data, extra = {}) => createOrUpdateCogs(brandId, [data], extra);
export const updateProductCost = updateCogs;
export const deleteProductCost = deleteCogs;

export const getBundles = async (brandId, params = {}) => {
  const response = await api.get(`${API_BASE}/api/cogs/brands/${brandId}/bundles`, { params });
  return response.data;
};

export const updateBundle = async (brandId, bundleSku, data) => {
  const response = await api.put(`${API_BASE}/api/cogs/brands/${brandId}/bundles/${encodeURIComponent(bundleSku)}`, data);
  return response.data;
};

export const deleteBundle = async (brandId, bundleId) => {
  const response = await api.delete(`${API_BASE}/api/cogs/brands/${brandId}/bundles/${bundleId}`);
  return response.data;
};

export const replaceBundleGeography = async (brandId, bundleSku, geography, data) => {
  const response = await api.post(
    `${API_BASE}/api/cogs/brands/${brandId}/bundles/${encodeURIComponent(bundleSku)}/geography/${geography}/replace`,
    data
  );
  return response.data;
};

// ============================================================================
// 90-DAY SALES DATA
// ============================================================================

export const get90DaySales = async (brandId) => {
  const response = await api.get(`${API_BASE}/api/cogs/brands/${brandId}/sales-90d`);
  return response.data;
};

// Per-SKU count of warehouses currently carrying stock (from the stock engine's daily snapshot)
export const getWarehouseCounts = async (brandId) => {
  const response = await api.get(`${API_BASE}/api/cogs/brands/${brandId}/warehouse-counts`);
  return response.data;
};

// ============================================================================
// WAREHOUSE TRANSFERS
// ============================================================================

export const getTransfers = async (params = {}) => {
  const response = await api.get(`${API_BASE}/api/warehouse-transfers`, { params });
  return response.data;
};

export const getTransfer = async (id) => {
  const response = await api.get(`${API_BASE}/api/warehouse-transfers/${id}`);
  return response.data;
};

export const createTransfer = async (data) => {
  const response = await api.post(`${API_BASE}/api/warehouse-transfers`, data);
  return response.data;
};

export const previewAllocation = async (data) => {
  const response = await api.post(`${API_BASE}/api/warehouse-transfers/preview-allocation`, data);
  return response.data;
};

export const shipTransfer = async (id, data = {}) => {
  const response = await api.post(`${API_BASE}/api/warehouse-transfers/${id}/ship`, data);
  return response.data;
};

export const receiveTransfer = async (id, data = {}) => {
  const response = await api.post(`${API_BASE}/api/warehouse-transfers/${id}/receive`, data);
  return response.data;
};

export const cancelTransfer = async (id) => {
  const response = await api.post(`${API_BASE}/api/warehouse-transfers/${id}/cancel`);
  return response.data;
};

// RC4: operator-driven status reversal — flips in_transit → draft (reverse-ship)
// or completed → in_transit (reverse-receive). Triggered from the entry form
// when the operator clears the Dispatch Date or Completed Date respectively.
export const reverseShipTransfer = async (id) => {
  const response = await api.post(`${API_BASE}/api/warehouse-transfers/${id}/reverse-ship`);
  return response.data;
};

export const reverseReceiveTransfer = async (id) => {
  const response = await api.post(`${API_BASE}/api/warehouse-transfers/${id}/reverse-receive`);
  return response.data;
};

export const updateTransfer = async (id, data) => {
  const response = await api.put(`${API_BASE}/api/warehouse-transfers/${id}`, data);
  return response.data;
};

export const deleteTransfer = async (id) => {
  const response = await api.delete(`${API_BASE}/api/warehouse-transfers/${id}`);
  return response.data;
};

// ============================================================================
// ORDER FULFILLMENTS
// ============================================================================

export const getFulfillments = async (params = {}) => {
  const response = await api.get(`${API_BASE}/api/order-fulfillments`, { params });
  return response.data;
};

export const getFulfillment = async (id) => {
  const response = await api.get(`${API_BASE}/api/order-fulfillments/${id}`);
  return response.data;
};

export const createFulfillment = async (data) => {
  const response = await api.post(`${API_BASE}/api/order-fulfillments`, data);
  return response.data;
};

export const processFulfillments = async () => {
  const response = await api.post(`${API_BASE}/api/order-fulfillments/process`);
  return response.data;
};

export const getFulfillmentStats = async (params = {}) => {
  const response = await api.get(`${API_BASE}/api/order-fulfillments/stats`, { params });
  return response.data;
};

// ============================================================================
// INVENTORY RETURNS
// ============================================================================

export const getReturns = async (params = {}) => {
  const response = await api.get(`${API_BASE}/api/inventory-returns`, { params });
  return response.data;
};

export const getReturn = async (id) => {
  const response = await api.get(`${API_BASE}/api/inventory-returns/${id}`);
  return response.data;
};

export const createReturn = async (data) => {
  const response = await api.post(`${API_BASE}/api/inventory-returns`, data);
  return response.data;
};

export const receiveReturn = async (id, data = {}) => {
  const response = await api.post(`${API_BASE}/api/inventory-returns/${id}/receive`, data);
  return response.data;
};

export const processReturn = async (id, data = {}) => {
  const response = await api.post(`${API_BASE}/api/inventory-returns/${id}/process`, data);
  return response.data;
};

export const getReturnStats = async (params = {}) => {
  const response = await api.get(`${API_BASE}/api/inventory-returns/stats`, { params });
  return response.data;
};

// ============================================================================
// STORE FULFILLMENT CONFIG
// ============================================================================

export const getFulfillmentConfigs = async (params = {}) => {
  const response = await api.get(`${API_BASE}/api/store-fulfillment-config`, { params });
  return response.data;
};

export const getFulfillmentConfig = async (id) => {
  const response = await api.get(`${API_BASE}/api/store-fulfillment-config/${id}`);
  return response.data;
};

export const createFulfillmentConfig = async (data) => {
  const response = await api.post(`${API_BASE}/api/store-fulfillment-config`, data);
  return response.data;
};

export const updateFulfillmentConfig = async (id, data) => {
  const response = await api.put(`${API_BASE}/api/store-fulfillment-config/${id}`, data);
  return response.data;
};

// ============================================================================
// FBA CAPACITY LIMITS (human-entered from Seller Central Capacity Monitor)
// ============================================================================

export const getFbaCapacityMerchants = async () => {
  const response = await api.get(`${API_BASE}/api/fba-capacity-limits/merchants`);
  return response.data;
};

export const getFbaCapacityLimits = async (params = {}) => {
  const response = await api.get(`${API_BASE}/api/fba-capacity-limits`, { params });
  return response.data;
};

export const getFbaCapacityUtilization = async (params = {}) => {
  const response = await api.get(`${API_BASE}/api/fba-capacity-limits/utilization`, { params });
  return response.data;
};

export const createFbaCapacityLimit = async (data) => {
  const response = await api.post(`${API_BASE}/api/fba-capacity-limits`, data);
  return response.data;
};

// ============================================================================
// PHYSICAL COUNTS
// ============================================================================

export const getPhysicalCounts = async (params = {}) => {
  const response = await api.get(`${API_BASE}/api/physical-counts`, { params });
  return response.data;
};

export const getPhysicalCount = async (id) => {
  const response = await api.get(`${API_BASE}/api/physical-counts/${id}`);
  return response.data;
};

export const createPhysicalCount = async (data) => {
  const response = await api.post(`${API_BASE}/api/physical-counts`, data);
  return response.data;
};

export const startPhysicalCount = async (id) => {
  const response = await api.post(`${API_BASE}/api/physical-counts/${id}/start`);
  return response.data;
};

export const addCountItems = async (countId, items) => {
  const response = await api.post(`${API_BASE}/api/physical-counts/${countId}/items`, { items });
  return response.data;
};

export const addCountLine = async (countId, lineData) => {
  const response = await api.post(`${API_BASE}/api/physical-counts/${countId}/lines`, lineData);
  return response.data;
};

export const updateCountLine = async (countId, lineId, data) => {
  const response = await api.put(`${API_BASE}/api/physical-counts/${countId}/lines/${lineId}`, data);
  return response.data;
};

export const completePhysicalCount = async (id, data = {}) => {
  const response = await api.post(`${API_BASE}/api/physical-counts/${id}/complete`, data);
  return response.data;
};

export const approveAdjustments = async (id) => {
  const response = await api.post(`${API_BASE}/api/physical-counts/${id}/approve`);
  return response.data;
};

/**
 * Delete a single physical count and all related adjustments
 * @param {number} countId - The count ID to delete
 * @param {number} companyId - Company ID (optional if user context provides it)
 */
export const deletePhysicalCount = async (countId, companyId) => {
  const params = companyId ? { company_id: companyId } : {};
  const response = await api.delete(`${API_BASE}/api/physical-counts/${countId}`, { params });
  return response.data;
};

/**
 * Delete multiple physical counts in bulk
 * @param {Object} options - Deletion options
 * @param {Array<number>} options.count_ids - Specific count IDs to delete
 * @param {string} options.count_date - Delete all counts with this date (YYYY-MM-DD)
 * @param {string} options.warehouse_code - Optional warehouse filter (with count_date)
 * @param {number} options.company_id - Company ID
 */
export const deletePhysicalCountsBulk = async (options) => {
  const response = await api.delete(`${API_BASE}/api/physical-counts/bulk`, { data: options });
  return response.data;
};

/**
 * Get count items for a specific physical count
 * @param {number} countId - The count ID
 * @param {number} companyId - Company ID (optional)
 */
export const getPhysicalCountItems = async (countId, companyId) => {
  const params = companyId ? { company_id: companyId } : {};
  const response = await api.get(`${API_BASE}/api/physical-counts/${countId}/items`, { params });
  return response.data;
};

/**
 * Delete a specific SKU from a physical count
 * @param {number} countId - The count ID
 * @param {string} sku - SKU to delete
 * @param {number} companyId - Company ID (optional)
 */
export const deletePhysicalCountItem = async (countId, sku, companyId) => {
  const params = companyId ? { company_id: companyId } : {};
  const response = await api.delete(`${API_BASE}/api/physical-counts/${countId}/items/${encodeURIComponent(sku)}`, { params });
  return response.data;
};

/**
 * Update a specific SKU in a physical count
 * @param {number} countId - The count ID
 * @param {string} sku - SKU to update
 * @param {Object} updates - { counted_quantity, variance_reason }
 * @param {number} companyId - Company ID (optional)
 */
export const updatePhysicalCountItem = async (countId, sku, updates, companyId) => {
  const params = companyId ? { company_id: companyId } : {};
  const response = await api.put(`${API_BASE}/api/physical-counts/${countId}/items/${encodeURIComponent(sku)}`, updates, { params });
  return response.data;
};

/**
 * Preview CSV import - parse and validate without importing
 * @param {File} file - CSV file
 * @param {Object} options - { company_id, brand_id }
 */
export const previewPhysicalCountImport = async (file, options = {}) => {
  const formData = new FormData();
  formData.append('file', file);
  if (options.company_id) formData.append('company_id', options.company_id);
  if (options.brand_id) formData.append('brand_id', options.brand_id);
  const response = await api.post(`${API_BASE}/api/physical-counts/import/preview`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  });
  return response.data;
};

/**
 * Check for duplicate counts (existing counts for same warehouse+date)
 * @param {Array} checks - Array of { warehouse_code, count_date }
 * @param {number} companyId - Company ID
 */
export const checkDuplicateCounts = async (checks, companyId) => {
  const response = await api.post(`${API_BASE}/api/physical-counts/check-duplicates`, {
    checks,
    company_id: companyId
  });
  return response.data;
};

/**
 * Execute bulk import with mapped data
 * @param {Object} importData - { rows, company_id, source, original_filename }
 */
export const executePhysicalCountImport = async (importData) => {
  const response = await api.post(`${API_BASE}/api/physical-counts/import`, importData);
  return response.data;
};

// ============================================================================
// SUPPLIERS (from procurement)
// ============================================================================

export const getSuppliers = async (params = {}) => {
  const response = await api.get(`${API_BASE}/api/procurement/suppliers`, { params });
  return response.data;
};

export const getSupplier = async (id) => {
  const response = await api.get(`${API_BASE}/api/procurement/suppliers/${id}`);
  return response.data;
};

// ============================================================================
// GOODS RECEIPTS
// ============================================================================

export const getGoodsReceipts = async (params = {}) => {
  const response = await api.get(`${API_BASE}/api/procurement/goods-receipts`, { params });
  return response.data;
};

export const getGoodsReceipt = async (id) => {
  const response = await api.get(`${API_BASE}/api/procurement/goods-receipts/${id}`);
  return response.data;
};

// ============================================================================
// RECEIVE INVENTORY (for Stock Levels)
// ============================================================================

export const receiveBatch = async (data) => {
  const response = await api.post(`${API_BASE}/api/procurement/inventory-batches`, data);
  return response.data;
};

// ============================================================================
// CSV IMPORT/EXPORT
// ============================================================================

/**
 * Bulk import inventory batches from CSV
 * @param {File} file - CSV file
 * @param {number} brandId - Required brand ID
 */
export const bulkImportBatches = async (file, brandId) => {
  if (!brandId) {
    throw new Error('brand_id is required for batch import');
  }
  const formData = new FormData();
  formData.append('file', file);
  formData.append('brand_id', brandId);
  const response = await api.post(`${API_BASE}/api/procurement/inventory-batches/bulk-import`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  });
  return response.data;
};

/**
 * Bulk import warehouses from CSV
 */
export const bulkImportWarehouses = async (file) => {
  const formData = new FormData();
  formData.append('file', file);
  const response = await api.post(`${API_BASE}/api/warehouses/bulk-import`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  });
  return response.data;
};

/**
 * Bulk import returns from CSV
 * @param {File} file - CSV file
 * @param {string} warehouseCode - Required warehouse code
 * @param {string} platform - Optional platform (default: 'manual')
 */
export const bulkImportReturns = async (file, warehouseCode, platform = 'manual') => {
  if (!warehouseCode) {
    throw new Error('warehouse_code is required for returns import');
  }
  const formData = new FormData();
  formData.append('file', file);
  formData.append('warehouse_code', warehouseCode);
  formData.append('platform', platform);
  const response = await api.post(`${API_BASE}/api/inventory-returns/bulk-import`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  });
  return response.data;
};

/**
 * Bulk import physical count lines from CSV (legacy - for existing count)
 */
export const bulkImportCountLines = async (countId, file) => {
  const formData = new FormData();
  formData.append('file', file);
  const response = await api.post(`${API_BASE}/api/physical-counts/${countId}/lines/bulk-import`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  });
  return response.data;
};

/**
 * Bulk import physical counts from CSV
 * Creates new counts grouped by warehouse_code + count_date
 * Required columns: sku, counted_quantity, warehouse_code, count_date
 * Optional columns: batch_number, gtin, expiry_date
 */
export const bulkImportPhysicalCounts = async (file) => {
  const formData = new FormData();
  formData.append('file', file);
  const response = await api.post(`${API_BASE}/api/physical-counts/bulk-import`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  });
  return response.data;
};

/**
 * Download CSV template for physical count import
 */
export const downloadPhysicalCountTemplate = () => {
  window.open(`${API_BASE}/api/physical-counts/csv-template`, '_blank');
};

/**
 * Get CSV templates for different import types
 */
export const getCSVTemplates = () => ({
  inventory: {
    filename: 'inventory_batches_import_template.csv',
    content: `sku,warehouse_code,batch_number,quantity,unit_cost,gtin,expiry_date,notes
SKU-001,ITC,BATCH-ITC-SKU001-8886482915016-20241201,100,12.50,8886482915016,2025-12-31,Initial stock with GTIN
SKU-002,ITC,,50,8.75,,,Auto-generate batch number (no GTIN)
SKU-003,G3PL,BATCH-G3PL-SKU003-20241201,200,5.00,,2025-06-30,Perishable item without GTIN`
  },
  warehouses: {
    filename: 'warehouses_import_template.csv',
    content: `code,name,address,city,country,is_primary
ITC,ITC Warehouse,123 Storage Lane,Singapore,SG,true
G3PL,G3PL Warehouse,456 Distribution St,Kuala Lumpur,MY,false
EU-FBA,EU Amazon FBA,,,DE,false
US-FBA,US Amazon FBA,,,US,false`
  },
  returns: {
    filename: 'returns_import_template.csv',
    content: `order_id,sku,quantity,reason,condition
ORD-12345,SKU-001,1,Customer changed mind,sellable
ORD-12346,SKU-002,2,Defective product,damaged
ORD-12347,SKU-003,1,Wrong size,sellable`
  },
  physical_count: {
    filename: 'physical_count_import_template.csv',
    content: `sku,counted_quantity,warehouse_code,count_date,batch_number,gtin,expiry_date
SKU-001,100,ITC,2024-12-01,BATCH-ITC-SKU001-001,8886482915016,2025-12-31
SKU-002,50,ITC,2024-12-01,,,
SKU-003,200,G3PL,2024-12-01,BATCH-G3PL-SKU003-001,,2025-06-30`
  }
});

// ============================================================================
// CSV EXPORT FUNCTIONS
// ============================================================================

/**
 * Export inventory batches to CSV
 */
export const exportInventoryBatches = async (brandId, warehouseCode) => {
  let url = `${API_BASE}/api/procurement/inventory-batches/export?`;
  if (brandId) url += `brand_id=${brandId}&`;
  if (warehouseCode) url += `warehouse_code=${encodeURIComponent(warehouseCode)}`;
  window.open(url, '_blank');
};

/**
 * Export warehouses to CSV
 */
export const exportWarehouses = async () => {
  window.open(`${API_BASE}/api/warehouses/export`, '_blank');
};

/**
 * Export returns to CSV
 */
export const exportReturns = async (filters = {}) => {
  const params = new URLSearchParams();
  if (filters.brand_id) params.append('brand_id', filters.brand_id);
  if (filters.status) params.append('status', filters.status);
  if (filters.warehouse_code) params.append('warehouse_code', filters.warehouse_code);
  if (filters.from_date) params.append('from_date', filters.from_date);
  if (filters.to_date) params.append('to_date', filters.to_date);
  window.open(`${API_BASE}/api/inventory-returns/export?${params.toString()}`, '_blank');
};

/**
 * Export physical counts to CSV
 */
export const exportPhysicalCounts = async (filters = {}) => {
  const params = new URLSearchParams();
  if (filters.warehouse_code) params.append('warehouse_code', filters.warehouse_code);
  if (filters.status) params.append('status', filters.status);
  if (filters.date_from) params.append('date_from', filters.date_from);
  if (filters.date_to) params.append('date_to', filters.date_to);
  window.open(`${API_BASE}/api/physical-counts/export?${params.toString()}`, '_blank');
};

/**
 * Download a CSV template
 */
export const downloadCSVTemplate = (type) => {
  const templates = getCSVTemplates();
  const template = templates[type];
  if (!template) {
    throw new Error(`Unknown template type: ${type}`);
  }

  const blob = new Blob([template.content], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  link.href = URL.createObjectURL(blob);
  link.download = template.filename;
  link.click();
};

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Calculate total inventory value for a list of batches
 */
export const calculateInventoryValue = (batches) => {
  return batches.reduce((total, batch) => {
    const qty = parseFloat(batch.current_quantity) || 0;
    const cost = parseFloat(batch.unit_cost) || 0;
    return total + (qty * cost);
  }, 0);
};

/**
 * Group inventory by SKU across warehouses
 */
export const groupInventoryBySku = (batches) => {
  const grouped = {};
  batches.forEach(batch => {
    const sku = batch.sku;
    if (!grouped[sku]) {
      grouped[sku] = {
        sku,
        total_quantity: 0,
        total_value: 0,
        warehouses: {}
      };
    }
    const qty = parseFloat(batch.current_quantity) || 0;
    const cost = parseFloat(batch.unit_cost) || 0;
    grouped[sku].total_quantity += qty;
    grouped[sku].total_value += qty * cost;

    const warehouse = batch.warehouse_code;
    if (!grouped[sku].warehouses[warehouse]) {
      grouped[sku].warehouses[warehouse] = { quantity: 0, value: 0, batches: [] };
    }
    grouped[sku].warehouses[warehouse].quantity += qty;
    grouped[sku].warehouses[warehouse].value += qty * cost;
    grouped[sku].warehouses[warehouse].batches.push(batch);
  });
  return Object.values(grouped);
};

/**
 * Format currency value - defaults to USD for consistent display
 */
export const formatCurrency = (value, currency = 'USD') => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currency
  }).format(value || 0);
};

/**
 * Format number with commas
 */
export const formatNumber = (value) => {
  return new Intl.NumberFormat('en-GB').format(value || 0);
};

/**
 * Format date for display (YYYY-MM-DD format)
 */
export const formatDate = (dateString) => {
  if (!dateString || dateString === '1970-01-01 00:00:00') return 'N/A';
  const date = new Date(dateString);
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

// ============================================================================
// WAREHOUSE MAPPINGS
// ============================================================================

// Store Configs (deduction source per store)
export const getStoreConfigs = async () => {
  const response = await api.get(`${API_BASE}/api/warehouse-mappings/store-configs`);
  return response.data;
};

export const discoverStores = async () => {
  const response = await api.get(`${API_BASE}/api/store-fulfillment-config/discover`);
  return response.data;
};

export const createStoreConfig = async (data) => {
  const response = await api.post(`${API_BASE}/api/warehouse-mappings/store-configs`, data);
  return response.data;
};

export const updateStoreConfig = async (id, data) => {
  const response = await api.put(`${API_BASE}/api/warehouse-mappings/store-configs/${id}`, data);
  return response.data;
};

// Platform Location Mappings (Shopify locations -> our warehouses)
export const getPlatformLocationMappings = async (params = {}) => {
  const response = await api.get(`${API_BASE}/api/warehouse-mappings/platform-locations`, { params });
  return response.data;
};

export const createPlatformLocationMapping = async (data) => {
  const response = await api.post(`${API_BASE}/api/warehouse-mappings/platform-locations`, data);
  return response.data;
};

export const updatePlatformLocationMapping = async (id, data) => {
  const response = await api.put(`${API_BASE}/api/warehouse-mappings/platform-locations/${id}`, data);
  return response.data;
};

// ShipStation User Mappings (userId -> our warehouses)
export const getShipstationMappings = async () => {
  const response = await api.get(`${API_BASE}/api/warehouse-mappings/shipstation-users`);
  return response.data;
};

export const createShipstationMapping = async (data) => {
  const response = await api.post(`${API_BASE}/api/warehouse-mappings/shipstation-users`, data);
  return response.data;
};

export const updateShipstationMapping = async (id, data) => {
  const response = await api.put(`${API_BASE}/api/warehouse-mappings/shipstation-users/${id}`, data);
  return response.data;
};

// Cin7 Branch Mappings (Cin7 branchId -> our warehouses)
export const getCin7BranchMappings = async () => {
  const response = await api.get(`${API_BASE}/api/warehouse-mappings/cin7-branches`);
  return response.data;
};

export const createCin7BranchMapping = async (data) => {
  const response = await api.post(`${API_BASE}/api/warehouse-mappings/cin7-branches`, data);
  return response.data;
};

export const updateCin7BranchMapping = async (id, data) => {
  const response = await api.put(`${API_BASE}/api/warehouse-mappings/cin7-branches/${id}`, data);
  return response.data;
};

export const deleteCin7BranchMapping = async (id) => {
  const response = await api.delete(`${API_BASE}/api/warehouse-mappings/cin7-branches/${id}`);
  return response.data;
};

// Shiphero Warehouse Mappings (Shiphero warehouse_id -> our warehouses)
export const getShipheroMappings = async () => {
  const response = await api.get(`${API_BASE}/api/warehouse-mappings/shiphero-warehouses`);
  return response.data;
};

export const createShipheroMapping = async (data) => {
  const response = await api.post(`${API_BASE}/api/warehouse-mappings/shiphero-warehouses`, data);
  return response.data;
};

export const updateShipheroMapping = async (id, data) => {
  const response = await api.put(`${API_BASE}/api/warehouse-mappings/shiphero-warehouses/${id}`, data);
  return response.data;
};

export const deleteShipheroMapping = async (id) => {
  const response = await api.delete(`${API_BASE}/api/warehouse-mappings/shiphero-warehouses/${id}`);
  return response.data;
};

// ============================================================================
// INVENTORY ADJUSTMENTS
// ============================================================================

export const createOpeningStock = async (data) => {
  const response = await api.post(`${API_BASE}/api/inventory-adjustments/opening-stock`, data);
  return response.data;
};

export const getOpeningStockHistory = async (params = {}) => {
  const response = await api.get(`${API_BASE}/api/inventory-adjustments/opening-stock`, { params });
  return response.data;
};

export const uploadOpeningStockCSV = async (file, companyId, brandId, entryDate) => {
  const formData = new FormData();
  formData.append('csv_file', file);
  formData.append('company_id', companyId);
  formData.append('brand_id', brandId);
  formData.append('entry_date', entryDate);
  const response = await api.post(`${API_BASE}/api/inventory-adjustments/opening-stock/csv-upload`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  });
  return response.data;
};

/**
 * Create a batch adjustment (manual correction to a specific batch)
 * @param {Object} data - { source_batch_id, adjustment_quantity, adjustment_type, reason, adjustment_date }
 */
export const createBatchAdjustment = async (data) => {
  const response = await api.post(`${API_BASE}/api/inventory-adjustments/batch`, data);
  return response.data;
};

/**
 * Update batch information (batch_number, expiry_date)
 * @param {number} batchId - The goods_receipt_items.id
 * @param {Object} data - { batch_number?: string, expiry_date?: string (YYYY-MM-DD) or null }
 */
export const updateBatch = async (batchId, data) => {
  const response = await api.put(`${API_BASE}/api/stock-levels/batch/${batchId}`, data);
  return response.data;
};

/**
 * Set a warehouse's stock level for a SKU.
 *
 * `new_quantity` is the ABSOLUTE target on-hand, not a delta. The backend computes
 * delta = new_quantity - current engine stock and books only that: an increase becomes a
 * goods receipt (carrying unit_cost/expiry/batch_number), a decrease becomes a signed
 * inventory_adjustments variance, and a zero delta writes nothing.
 *
 * @param {Object} data - { sku, warehouse_code, brand_id, new_quantity, unit_cost?,
 *                          expiry_date?, batch_number?, reason?, receipt_date? }
 *   brand_id is REQUIRED — the receipt is stamped with it and the stock engine filters
 *   goods receipts by brand_id.
 */
export const adjustWarehouseStock = async (data) => {
  const response = await api.post(`${API_BASE}/api/stock-levels/warehouse/adjust`, data);
  return response.data;
};

/**
 * Set true stock for a SKU at a warehouse on a specific date.
 * Creates an inventory_adjustments row with the variance between system and true stock.
 * @param {Object} data - { sku, warehouse_code, effective_date, true_quantity, brand_id, reason? }
 */
export const setTrueStock = async (data) => {
  const response = await api.post(`${API_BASE}/api/stock-levels/set-true-stock`, data);
  return response.data;
};

/**
 * Apply a trust point — computes correct stock (anchored to trust point) vs system stock,
 * and inserts an additive adjustment at the trust point date if they differ.
 * @param {Object} data - { sku, warehouse_code, trust_point_type, trust_point_id,
 *                         trust_point_date, brand_id,
 *                         [override_quantity], [timing: 'end_of_day'|'start_of_day'] }
 */
export const applyTrustPoint = async (data) => {
  const response = await api.post(`${API_BASE}/api/stock-levels/apply-trust-point`, data);
  return response.data;
};

/**
 * Read-only preview of what applyTrustPoint would do.
 * Same body shape as applyTrustPoint. Returns existing adjustments that would be
 * deleted, the projected variance, and the adjustment that would be inserted.
 * Writes nothing.
 */
export const previewTrustPoint = async (data) => {
  const response = await api.post(`${API_BASE}/api/stock-levels/preview-trust-point`, data);
  return response.data;
};

/**
 * GET /api/stock-levels/trust-point-queue
 * Returns the worklist for the trust-point wizard:
 * one row per (sku, warehouse_code, brand_id) with >=2 physical counts,
 * excluding FBA + truly-manual-adjusted pairs. Ordered by last_count_date DESC.
 *
 * @param {Object} params { brand_id?, hide_processed?, limit? }
 * @returns {Promise<{ success, data, total }>}
 */
export const getTrustPointQueue = async (params = {}) => {
  const response = await api.get(`${API_BASE}/api/stock-levels/trust-point-queue`, { params });
  return response.data;
};

/**
 * POST /api/stock-levels/backup-adjustments
 * Idempotent daily snapshot of the inventory_adjustments table per company DB.
 * The wizard requires today's snapshot to exist before any Apply click.
 *
 * @returns {Promise<{ success, already_exists, table_name, row_count, created_at }>}
 */
export const backupAdjustments = async () => {
  const response = await api.post(`${API_BASE}/api/stock-levels/backup-adjustments`);
  return response.data;
};

/**
 * POST /api/stock-levels/accept-current-trust-point
 * Inserts a zero-variance marker row tagged `manual_wizard:accepted` so the row
 * counts as already-processed by the wizard's queue endpoint without changing stock.
 *
 * @param {Object} data - { sku, warehouse_code, brand_id }
 * @returns {Promise<{ success, data: { sku, warehouse_code, brand_id, accepted_existing, marker_id } }>}
 */
export const acceptCurrentTrustPoint = async (data) => {
  const response = await api.post(`${API_BASE}/api/stock-levels/accept-current-trust-point`, data);
  return response.data;
};

// ============================================================================
// BATCH MANAGEMENT (Full Batch View & Edit)
// ============================================================================

/**
 * Get all batches with aggregated data
 * @param {Object} params - { brand_id?, sku?, batch_number?, limit?, offset? }
 */
export const getAllBatches = async (params = {}) => {
  const response = await api.get(`${API_BASE}/api/batches`, { params });
  return response.data;
};

/**
 * Get single batch details with full warehouse breakdown
 * @param {string} batchNumber - The batch number
 * @param {string} sku - The SKU
 */
export const getBatchDetails = async (batchNumber, sku) => {
  const response = await api.get(`${API_BASE}/api/batches/${encodeURIComponent(batchNumber)}/${encodeURIComponent(sku)}`);
  return response.data;
};

/**
 * Get batch history - all receipts and transfers
 * @param {string} batchNumber - The batch number
 * @param {string} sku - The SKU
 */
export const getBatchHistory = async (batchNumber, sku) => {
  const response = await api.get(`${API_BASE}/api/batches/${encodeURIComponent(batchNumber)}/${encodeURIComponent(sku)}/history`);
  return response.data;
};

/**
 * Update a single batch item (goods receipt item)
 * @param {number} itemId - The goods_receipt_items.id
 * @param {Object} data - { batch_number?, expiry_date?, manufacturing_date?, bin_location?, notes?, sku?, gtin?, asin? }
 */
export const updateBatchItem = async (itemId, data) => {
  const response = await api.put(`${API_BASE}/api/batches/item/${itemId}`, data);
  return response.data;
};

/**
 * Bulk update all items with the same batch_number and sku
 * @param {string} batchNumber - The batch number
 * @param {string} sku - The SKU
 * @param {Object} data - { new_batch_number?, expiry_date?, manufacturing_date?, receipt_date? }
 */
export const updateBatchBulk = async (batchNumber, sku, data) => {
  const response = await api.put(`${API_BASE}/api/batches/${encodeURIComponent(batchNumber)}/${encodeURIComponent(sku)}`, data);
  return response.data;
};

/**
 * Delete a single batch item (goods receipt item)
 * @param {number} itemId - The goods_receipt_items.id
 */
export const deleteBatchItem = async (itemId) => {
  const response = await api.delete(`${API_BASE}/api/batches/item/${itemId}`);
  return response.data;
};

/**
 * Delete all items with the same batch_number and sku
 * @param {string} batchNumber - The batch number
 * @param {string} sku - The SKU
 */
export const deleteBatchBulk = async (batchNumber, sku) => {
  const response = await api.delete(`${API_BASE}/api/batches/${encodeURIComponent(batchNumber)}/${encodeURIComponent(sku)}`);
  return response.data;
};

/**
 * Export batches to CSV file
 * @param {number} brandId - Optional brand ID to filter
 */
export const exportBatchesCSV = async (brandId) => {
  let url = `${API_BASE}/api/batches/export/csv`;
  if (brandId) url += `?brand_id=${brandId}`;

  try {
    const response = await api.get(url, { responseType: 'blob' });

    // Create download link
    const blob = new Blob([response.data], { type: 'text/csv' });
    const downloadUrl = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = downloadUrl;

    // Get filename from content-disposition header or use default
    const contentDisposition = response.headers['content-disposition'];
    let filename = 'batches_export.csv';
    if (contentDisposition) {
      const match = contentDisposition.match(/filename="?(.+)"?/);
      if (match && match[1]) {
        filename = match[1];
      }
    }

    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(downloadUrl);
  } catch (error) {
    console.error('Error exporting batches:', error);
    throw error;
  }
};

/**
 * Import/Update batches from CSV file
 * @param {File} file - CSV file
 * @param {number} brandId - Optional brand ID for new entries
 */
export const importBatchesCSV = async (file, brandId) => {
  const formData = new FormData();
  formData.append('file', file);
  if (brandId) formData.append('brand_id', brandId);
  const response = await api.post(`${API_BASE}/api/batches/import/csv`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  });
  return response.data;
};

/**
 * Download CSV template for batch import
 */
export const downloadBatchCSVTemplate = async () => {
  try {
    const response = await api.get(`${API_BASE}/api/batches/template/csv`, { responseType: 'blob' });

    const blob = new Blob([response.data], { type: 'text/csv' });
    const downloadUrl = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = downloadUrl;
    link.download = 'batch_import_template.csv';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(downloadUrl);
  } catch (error) {
    console.error('Error downloading template:', error);
    throw error;
  }
};

/**
 * Get adjustments for a specific batch
 * @param {number} batchId - The goods_receipt_items.id
 */
export const getBatchAdjustments = async (batchId) => {
  const response = await api.get(`${API_BASE}/api/inventory-adjustments/batch/${batchId}`);
  return response.data;
};

// ============================================================================
// INVENTORY DISPOSALS
// ============================================================================

export const getDisposals = async (params = {}) => {
  const response = await api.get(`${API_BASE}/api/inventory-disposals`, { params });
  return response.data;
};

export const getDisposal = async (id) => {
  const response = await api.get(`${API_BASE}/api/inventory-disposals/${id}`);
  return response.data;
};

export const createDisposal = async (data) => {
  const response = await api.post(`${API_BASE}/api/inventory-disposals`, data);
  return response.data;
};

export const getDisposalSummary = async (params = {}) => {
  const response = await api.get(`${API_BASE}/api/inventory-disposals/summary`, { params });
  return response.data;
};

export const getExpiredBatchesForDisposal = async (params = {}) => {
  const response = await api.get(`${API_BASE}/api/inventory-disposals/expired-batches`, { params });
  return response.data;
};

export const bulkDisposeExpired = async (data) => {
  const response = await api.post(`${API_BASE}/api/inventory-disposals/bulk-expire`, data);
  return response.data;
};

// Bulk-create disposals from a parsed CSV. `rows` is an array of
// { warehouse_code, sku, quantity, disposal_type, disposal_date, unit_cost?, reason?, batch_number?, reference_number?, notes?, brand_id? }.
// Returns { success, attempted, created, failed, results:[{row, sku, id?, total_value?, error?}] }.
export const bulkCreateDisposals = async (rows) => {
  const response = await api.post(`${API_BASE}/api/inventory-disposals/bulk`, { rows });
  return response.data;
};

export const updateDisposal = async (id, data) => {
  const response = await api.put(`${API_BASE}/api/inventory-disposals/${id}`, data);
  return response.data;
};

export const deleteDisposal = async (id) => {
  const response = await api.delete(`${API_BASE}/api/inventory-disposals/${id}`);
  return response.data;
};

// ============================================================================
// INVENTORY MASTER REPORT
// ============================================================================

/**
 * Get inventory master report (hierarchical format grouped by category)
 * @param {Object} params - { brand_id (required), as_of_date }
 */
export const getInventoryMasterReport = async (params = {}) => {
  const response = await api.get(`${API_BASE}/api/inventory-master`, { params });
  return response.data;
};

/**
 * Get inventory master report in flat format (for spreadsheet display)
 * Each row is a SKU + geography combination
 * @param {Object} params - { brand_id (required), as_of_date }
 */
export const getInventoryMasterFlat = async (params = {}) => {
  const response = await api.get(`${API_BASE}/api/inventory-master/flat`, { params });
  return response.data;
};

/**
 * Get run rates (D7, D14, D30 daily averages) by SKU and geography
 * @param {Object} params - { brand_id (required), as_of_date }
 */
export const getRunRates = async (params = {}) => {
  const response = await api.get(`${API_BASE}/api/inventory-master/run-rates`, { params });
  return response.data;
};

// ============================================================================
// BATCH RECONCILIATION
// ============================================================================

/**
 * Get reconciliation status - how many unprocessed shipments remain
 * @param {Object} params - { brand_id? }
 */
export const getReconciliationStatus = async (params = {}) => {
  const response = await api.get(`${API_BASE}/api/batches/reconciliation/status`, { params });
  return response.data;
};

/**
 * Run batch reconciliation - process shipments to update inventory_batches
 * @param {Object} params - { brand_id?, start_date?, limit? }
 */
export const runReconciliation = async (params = {}) => {
  const response = await api.post(`${API_BASE}/api/batches/reconciliation/run`, params);
  return response.data;
};

/**
 * Get SKUs that failed reconciliation due to missing batch mappings
 * @param {Object} params - { limit?, offset?, include_mapped? }
 */
export const getReconciliationUnmappedSkus = async (params = {}) => {
  const response = await api.get(`${API_BASE}/api/batches/reconciliation/unmapped-skus`, { params });
  return response.data;
};

/**
 * Get summary of unmapped SKUs (counts by source type)
 */
export const getReconciliationUnmappedSummary = async () => {
  const response = await api.get(`${API_BASE}/api/batches/reconciliation/unmapped-summary`);
  return response.data;
};

/**
 * Map a reconciliation unmapped SKU to a batch inventory SKU
 * @param {Object} data - { unmapped_sku_id, mapped_to_sku }
 */
export const mapReconciliationSku = async (data) => {
  const response = await api.post(`${API_BASE}/api/batches/reconciliation/map-sku`, data);
  return response.data;
};

// ============================================================================
// INVENTORY INTELLIGENCE DASHBOARD (Company-Wide)
// ============================================================================

/**
 * Get company-wide inventory summary with per-brand breakdown
 * This is the main endpoint for the Intelligence dashboard
 * Returns totals + brand array (NO brand_id required - shows ALL brands)
 * Uses caching (5 min TTL) for fast response
 */
export const getCompanySummary = async () => {
  const response = await api.get(`${API_BASE}/api/inventory/company-summary`);
  return response.data;
};

/**
 * Get single brand inventory summary
 * @param {number} brandId - Brand ID
 */
export const getBrandSummary = async (brandId) => {
  const response = await api.get(`${API_BASE}/api/inventory/brand-summary/${brandId}`);
  return response.data;
};

/**
 * Get brands that need attention (low stock or expiring)
 * Returns only brands with issues, sorted by urgency
 */
export const getBrandsNeedingAttention = async () => {
  const response = await api.get(`${API_BASE}/api/inventory/brands-attention`);
  return response.data;
};

/**
 * Get low stock items for a SINGLE brand
 * @param {number} brandId - Brand ID
 * @param {number} limit - Max results (default 50)
 */
export const getBrandLowStock = async (brandId, limit = 50) => {
  const response = await api.get(`${API_BASE}/api/inventory/low-stock/${brandId}`, {
    params: { limit }
  });
  return response.data;
};

/**
 * Get expiring inventory for a SINGLE brand
 * @param {number} brandId - Brand ID
 * @param {number} days - Days until expiry threshold (default 60)
 * @param {number} limit - Max results (default 50)
 */
export const getBrandExpiringStock = async (brandId, days = 60, limit = 50) => {
  const response = await api.get(`${API_BASE}/api/inventory/expiring/${brandId}`, {
    params: { days, limit }
  });
  return response.data;
};

/**
 * Get expiring inventory across ALL brands
 * @param {number} days - Days until expiry threshold (default 60)
 * @param {number} limit - Max results (default 50)
 */
export const getCompanyExpiringStock = async (days = 60, limit = 50) => {
  const response = await api.get(`${API_BASE}/api/inventory/expiring`, {
    params: { days, limit }
  });
  return response.data;
};

/**
 * Get inventory alerts for a single brand using MSR calculations
 * Returns ROD-based stockout alerts and expiry alerts
 *
 * @param {number} brandId - Brand ID
 */
export const getBrandAlerts = async (brandId) => {
  const response = await api.get(`${API_BASE}/api/inventory/alerts/${brandId}`);
  return response.data;
};

/**
 * Get company-wide alerts summary
 * Returns aggregated alerts across all brands
 *
 * NOTE: This can be slow for companies with many brands.
 */
export const getCompanyAlerts = async () => {
  const response = await api.get(`${API_BASE}/api/inventory/alerts`);
  return response.data;
};

// ============================================================================
// AMAZON RESTOCK RECOMMENDATIONS (Cross-Brand FBA Replenishment)
// ============================================================================

/**
 * Get cross-brand restock recommendations summary
 * @param {Object} params - Filtering options
 * @param {number} params.brand_id - Filter by brand ID (optional)
 * @param {string} params.alert_type - Filter by alert type: out_of_stock, low_stock (optional)
 */
export const getRestockSummary = async (params = {}) => {
  const response = await api.get(`${API_BASE}/api/amazon-restock/summary`, { params });
  return response.data;
};

/**
 * Get detailed restock recommendations with pagination
 * @param {Object} params - Filtering and pagination options
 * @param {number} params.brand_id - Filter by brand ID
 * @param {string} params.alert_type - Filter by alert type
 * @param {string} params.country - Filter by country
 * @param {string} params.search - Search SKU, ASIN, or product name
 * @param {number} params.limit - Number of results
 * @param {number} params.offset - Pagination offset
 * @param {string} params.sort_by - Column to sort by
 * @param {string} params.sort_order - ASC or DESC
 */
export const getRestockDetails = async (params = {}) => {
  const response = await api.get(`${API_BASE}/api/amazon-restock/details`, { params });
  return response.data;
};

/**
 * Get local warehouse availability for FBA replenishment
 * @param {Object} params - Filtering options
 * @param {number} params.brand_id - Filter by brand ID (optional)
 * @param {string} params.country - Filter by FBA country (optional)
 * @param {string} params.sku - Filter by SKU (optional)
 */
export const getRestockLocalAvailability = async (params = {}) => {
  const response = await api.get(`${API_BASE}/api/amazon-restock/local-availability`, { params });
  return response.data;
};

/**
 * Get monthly revenue impact projection
 * @param {Object} params - Filtering options
 * @param {number} params.brand_id - Filter by brand ID (optional)
 */
export const getRestockRevenueImpact = async (params = {}) => {
  const response = await api.get(`${API_BASE}/api/amazon-restock/revenue-impact`, { params });
  return response.data;
};

/**
 * Get alert counts for navigation badge
 */
export const getRestockAlertCounts = async () => {
  const response = await api.get(`${API_BASE}/api/amazon-restock/alert-counts`);
  return response.data;
};

/**
 * Export restock data to CSV
 * @param {Object} params - Filtering options (same as getRestockDetails)
 */
export const exportRestockCSV = async (params = {}) => {
  try {
    const response = await api.get(`${API_BASE}/api/amazon-restock/export`, {
      params,
      responseType: 'blob'
    });

    // Create download link
    const blob = new Blob([response.data], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;

    // Generate filename with date
    const date = new Date().toISOString().split('T')[0];
    link.download = `amazon_restock_${date}.csv`;

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);

    return { success: true };
  } catch (error) {
    console.error('Error exporting restock CSV:', error);
    throw error;
  }
};

/**
 * Clear restock data cache for fresh data
 */
export const clearRestockCache = async () => {
  const response = await api.post(`${API_BASE}/api/amazon-restock/clear-cache`);
  return response.data;
};

/**
 * Clear the company summary cache (admin only)
 */
export const clearSummaryCache = async () => {
  const response = await api.post(`${API_BASE}/api/inventory/clear-cache`);
  return response.data;
};

// ============================================================================
// INVENTORY MOVEMENTS (AUDIT TRAIL)
// ============================================================================

export const getInventoryMovements = async (brandId, params = {}) => {
  const response = await api.get(`${API_BASE}/api/brands/${brandId}/inventory-movements`, { params });
  return response.data;
};

export const getInventoryMovementsSummary = async (brandId, params = {}) => {
  const response = await api.get(`${API_BASE}/api/brands/${brandId}/inventory-movements/summary`, { params });
  return response.data;
};

export const getMonthlyShipmentSummary = async (brandId, params = {}) => {
  const response = await api.get(`${API_BASE}/api/brands/${brandId}/inventory-movements/monthly-shipments`, { params });
  return response.data;
};

export const getShipmentOrderDetails = async (brandId, params = {}) => {
  const response = await api.get(`${API_BASE}/api/brands/${brandId}/inventory-movements/shipment-orders`, { params });
  return response.data;
};

export const getDailyAggregatedMovements = async (brandId, params = {}) => {
  const response = await api.get(`${API_BASE}/api/brands/${brandId}/inventory-movements/daily`, { params });
  return response.data;
};

export const getDailyMovementDetails = async (brandId, params = {}) => {
  const response = await api.get(`${API_BASE}/api/brands/${brandId}/inventory-movements/daily-details`, { params });
  return response.data;
};

// ============================================================================
// FBA SHIPMENT DISCREPANCIES
// ============================================================================

/**
 * Get summary of FBA shipment discrepancies by brand
 */
export const getFbaDiscrepancySummary = async () => {
  const response = await api.get(`${API_BASE}/api/fba-discrepancies/summary`);
  return response.data;
};

/**
 * Get list of FBA shipments with discrepancies
 * @param {Object} params - Filtering and pagination options
 * @param {string} params.brand - Filter by brand name
 * @param {string} params.sku - Filter by SKU (partial match)
 * @param {string} params.shipment_id - Filter by shipment ID (partial match)
 * @param {number} params.min_missing - Minimum missing units threshold
 * @param {string} params.sort_by - Column to sort by
 * @param {string} params.sort_order - ASC or DESC
 * @param {number} params.limit - Number of results
 * @param {number} params.offset - Pagination offset
 */
export const getFbaDiscrepancyShipments = async (params = {}) => {
  const response = await api.get(`${API_BASE}/api/fba-discrepancies/shipments`, { params });
  return response.data;
};

/**
 * Get detailed information for a specific FBA shipment
 * @param {string} shipmentId - The shipment ID
 */
export const getFbaShipmentDetails = async (shipmentId) => {
  const response = await api.get(`${API_BASE}/api/fba-discrepancies/shipments/${encodeURIComponent(shipmentId)}`);
  return response.data;
};

/**
 * Get list of available brands for filtering FBA discrepancies
 */
export const getFbaDiscrepancyBrands = async () => {
  const response = await api.get(`${API_BASE}/api/fba-discrepancies/brands`);
  return response.data;
};

/**
 * Upload CSV file with FBA shipment dates
 * @param {File} file - CSV file
 * @param {Object} options - { updateExisting: boolean }
 */
export const uploadFbaShipmentCSV = async (file, options = {}) => {
  const formData = new FormData();
  formData.append('file', file);
  if (options.updateExisting !== undefined) {
    formData.append('updateExisting', options.updateExisting.toString());
  }
  const response = await api.post(`${API_BASE}/api/fba-discrepancies/csv/upload`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  });
  return response.data;
};

/**
 * Preview CSV file before uploading (dry run)
 * @param {File} file - CSV file
 */
export const previewFbaShipmentCSV = async (file) => {
  const formData = new FormData();
  formData.append('file', file);
  const response = await api.post(`${API_BASE}/api/fba-discrepancies/csv/preview`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  });
  return response.data;
};

/**
 * Get statistics about uploaded FBA shipment CSV data
 */
export const getFbaShipmentCSVStats = async () => {
  const response = await api.get(`${API_BASE}/api/fba-discrepancies/csv/stats`);
  return response.data;
};

/**
 * Export FBA discrepancies with breakdown to CSV
 * Returns CSV file with shipment summary rows followed by item detail rows
 * @param {Object} params - Filtering options
 * @param {string} params.brand - Filter by brand name
 * @param {string} params.sku - Filter by SKU (partial match)
 * @param {string} params.shipment_id - Filter by shipment ID (partial match)
 * @param {number} params.min_missing - Minimum missing units threshold
 */
export const exportFbaDiscrepancies = async (params = {}) => {
  const response = await api.get(`${API_BASE}/api/fba-discrepancies/export`, {
    params,
    responseType: 'blob'
  });

  // Create download link
  const url = window.URL.createObjectURL(new Blob([response.data]));
  const link = document.createElement('a');
  link.href = url;

  // Extract filename from response headers or use default
  const contentDisposition = response.headers['content-disposition'];
  let filename = 'fba_discrepancies_breakdown.csv';
  if (contentDisposition) {
    const filenameMatch = contentDisposition.match(/filename="?(.+)"?/i);
    if (filenameMatch) {
      filename = filenameMatch[1];
    }
  }

  link.setAttribute('download', filename);
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
};

export default {
  // Warehouses
  getWarehouses,
  getWarehouse,
  getWarehouseByCode,
  createWarehouse,
  updateWarehouse,
  deactivateWarehouse,
  reactivateWarehouse,
  getWarehouseInventory,
  // Batches
  getInventoryBatches,
  getExpiringBatches,
  // External Stock
  getExternalStockLevels,
  getExternalWarehouses,
  // Calculated Stock Levels
  getCalculatedStockLevels,
  getStockSummary,
  getDashboardSummary,
  getSkuBatches,
  getBulkSkuBatches,
  getShippedOrdersBySku,
  exportStockLevels,
  exportStockLevelsDetailed,
  // COGS
  getCogs,
  createOrUpdateCogs,
  updateCogs,
  deleteCogs,
  getCogsHistory,
  createBundle,
  bulkImportCogs,
  getAliases,
  createAlias,
  deleteAlias,
  // Transfers
  getTransfers,
  getTransfer,
  createTransfer,
  previewAllocation,
  shipTransfer,
  receiveTransfer,
  cancelTransfer,
  updateTransfer,
  deleteTransfer,
  // Fulfillments
  getFulfillments,
  getFulfillment,
  createFulfillment,
  processFulfillments,
  getFulfillmentStats,
  // Returns
  getReturns,
  getReturn,
  createReturn,
  receiveReturn,
  processReturn,
  getReturnStats,
  // Fulfillment Config
  getFulfillmentConfigs,
  getFulfillmentConfig,
  createFulfillmentConfig,
  updateFulfillmentConfig,
  // Physical Counts
  getPhysicalCounts,
  getPhysicalCount,
  createPhysicalCount,
  startPhysicalCount,
  addCountItems,
  addCountLine,
  updateCountLine,
  completePhysicalCount,
  approveAdjustments,
  deletePhysicalCount,
  deletePhysicalCountsBulk,
  getPhysicalCountItems,
  deletePhysicalCountItem,
  updatePhysicalCountItem,
  previewPhysicalCountImport,
  checkDuplicateCounts,
  executePhysicalCountImport,
  // Suppliers
  getSuppliers,
  getSupplier,
  // Goods Receipts
  getGoodsReceipts,
  getGoodsReceipt,
  // CSV Import/Export
  receiveBatch,
  bulkImportBatches,
  bulkImportWarehouses,
  bulkImportReturns,
  bulkImportCountLines,
  bulkImportPhysicalCounts,
  downloadPhysicalCountTemplate,
  getCSVTemplates,
  downloadCSVTemplate,
  // CSV Export Functions
  exportInventoryBatches,
  exportWarehouses,
  exportReturns,
  exportPhysicalCounts,
  // Inventory Adjustments
  createOpeningStock,
  getOpeningStockHistory,
  uploadOpeningStockCSV,
  createBatchAdjustment,
  updateBatch,
  adjustWarehouseStock,
  getBatchAdjustments,
  // Batch Management (Full View & Edit)
  getAllBatches,
  getBatchDetails,
  getBatchHistory,
  updateBatchItem,
  updateBatchBulk,
  deleteBatchItem,
  deleteBatchBulk,
  exportBatchesCSV,
  importBatchesCSV,
  downloadBatchCSVTemplate,
  // Inventory Movements
  getInventoryMovements,
  getInventoryMovementsSummary,
  getMonthlyShipmentSummary,
  getShipmentOrderDetails,
  getDailyAggregatedMovements,
  getDailyMovementDetails,
  // FBA Shipment Discrepancies
  getFbaDiscrepancySummary,
  getFbaDiscrepancyShipments,
  getFbaShipmentDetails,
  getFbaDiscrepancyBrands,
  uploadFbaShipmentCSV,
  previewFbaShipmentCSV,
  getFbaShipmentCSVStats,
  exportFbaDiscrepancies,
  // Inventory Master Report
  getInventoryMasterReport,
  getInventoryMasterFlat,
  getRunRates,
  // Stock Count Dates & Trust Points
  getStockCountDates,
  getTrustPoints,
  getStockBreakdownFromTrustPoint,
  // Batch Reconciliation
  getReconciliationStatus,
  runReconciliation,
  getReconciliationUnmappedSkus,
  getReconciliationUnmappedSummary,
  mapReconciliationSku,
  // Inventory Intelligence Dashboard (Company-Wide)
  getCompanySummary,
  getBrandsNeedingAttention,
  getCompanyExpiringStock,
  // MSR-based Alerts (ROD/velocity-based detection)
  getBrandAlerts,
  getCompanyAlerts,
  // Amazon Restock Recommendations
  getRestockSummary,
  getRestockDetails,
  getRestockLocalAvailability,
  getRestockRevenueImpact,
  getRestockAlertCounts,
  exportRestockCSV,
  clearRestockCache,
  // Helpers
  calculateInventoryValue,
  groupInventoryBySku,
  formatCurrency,
  formatNumber,
  formatDate
};

